import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const Privacy = () => {
  return (
    <>
      <SEOHead 
        title="Privacy Policy | uplino.in"
        description="Learn how uplino.in collects, uses, and protects your personal information. Our commitment to your privacy and data security."
        keywords={["privacy policy", "data protection", "user privacy", "uplino privacy"]}
        canonical="https://uplino.in/privacy"
      />
      <div className="min-h-screen bg-slate-950">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-400 mb-6">
              Last Updated: December 2025
            </p>

            <div className="bg-gradient-to-r from-rose-900/20 to-amber-900/20 border border-white/10 rounded-xl p-6 mb-8">
              <p className="text-gray-300">
                At uplino.in, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you use our platform.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">1. Information We Collect</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">1.1 Personal Information</h3>
                  <p className="text-gray-300 mb-2">Information you provide directly:</p>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Name and contact details (email, phone number)</li>
                    <li>Account credentials (username, password)</li>
                    <li>Profile information</li>
                    <li>Payment and transaction information</li>
                    <li>Content you upload (ads, images, descriptions)</li>
                    <li>Communications with our support team</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">1.2 Automatically Collected Information</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Operating system</li>
                    <li>Pages visited and time spent</li>
                    <li>Click patterns and interactions</li>
                    <li>Referral source</li>
                    <li>Device fingerprint (for fraud prevention)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">1.3 Cookies and Tracking</h3>
                  <p className="text-gray-300">
                    We use cookies and similar technologies to enhance your experience, remember 
                    your preferences, analyze site traffic, and for security purposes.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li><span className="text-white">Account Management:</span> Create and manage your account, authenticate logins</li>
                  <li><span className="text-white">Service Delivery:</span> Process and display your advertisements</li>
                  <li><span className="text-white">Payments:</span> Process wallet transactions and maintain records</li>
                  <li><span className="text-white">Communication:</span> Send notifications, updates, and respond to inquiries</li>
                  <li><span className="text-white">Improvements:</span> Analyze usage patterns to improve our services</li>
                  <li><span className="text-white">Security:</span> Detect and prevent fraud, abuse, and security threats</li>
                  <li><span className="text-white">Legal Compliance:</span> Comply with applicable laws and regulations</li>
                  <li><span className="text-white">Marketing:</span> Send promotional content (with your consent)</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">3. Information Sharing</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">3.1 Public Information</h3>
                  <p className="text-gray-300">
                    Information you include in your advertisements (contact details, descriptions) 
                    will be publicly visible to other users of the platform.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">3.2 We May Share With</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li><span className="text-white">Service Providers:</span> Third parties who help us operate (hosting, analytics, payment processing)</li>
                    <li><span className="text-white">Legal Requirements:</span> When required by law, court order, or government request</li>
                    <li><span className="text-white">Protection:</span> To protect rights, safety, and property of users and the platform</li>
                    <li><span className="text-white">Business Transfers:</span> In connection with merger, acquisition, or sale of assets</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">3.3 We Do NOT</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Sell your personal information to third parties</li>
                    <li>Share your data for third-party marketing without consent</li>
                    <li>Provide your contact details to other advertisers</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">4. Data Security</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">
                  We implement appropriate technical and organizational measures to protect your data:
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li>SSL/TLS encryption for data transmission</li>
                  <li>Encrypted password storage (bcrypt hashing)</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Access controls and authentication</li>
                  <li>Secure data centers with physical security</li>
                  <li>Regular backups and disaster recovery</li>
                </ul>
                <p className="text-gray-300 mt-3">
                  However, no method of transmission over the Internet is 100% secure. We cannot 
                  guarantee absolute security of your data.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">5. Data Retention</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">We retain your information as follows:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li><span className="text-white">Account Data:</span> Until you delete your account or request deletion</li>
                  <li><span className="text-white">Advertisements:</span> 90 days after expiration</li>
                  <li><span className="text-white">Transaction Records:</span> 7 years (legal/tax requirements)</li>
                  <li><span className="text-white">Communication Logs:</span> 2 years</li>
                  <li><span className="text-white">Analytics Data:</span> 2 years (anonymized thereafter)</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">You have the right to:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li><span className="text-white">Access:</span> Request a copy of your personal data</li>
                  <li><span className="text-white">Correction:</span> Request correction of inaccurate data</li>
                  <li><span className="text-white">Deletion:</span> Request deletion of your data (subject to legal requirements)</li>
                  <li><span className="text-white">Portability:</span> Receive your data in a machine-readable format</li>
                  <li><span className="text-white">Opt-out:</span> Unsubscribe from marketing communications</li>
                  <li><span className="text-white">Withdraw Consent:</span> Withdraw previously given consent</li>
                </ul>
                <p className="text-gray-300 mt-3">
                  To exercise these rights, contact us at privacy@uplino.in
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">7. Cookies Policy</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Types of Cookies We Use</h3>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li><span className="text-white">Essential:</span> Required for basic functionality (login, security)</li>
                    <li><span className="text-white">Functional:</span> Remember your preferences</li>
                    <li><span className="text-white">Analytics:</span> Understand how you use our site</li>
                    <li><span className="text-white">Performance:</span> Monitor and improve performance</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Managing Cookies</h3>
                  <p className="text-gray-300">
                    You can control cookies through your browser settings. Note that disabling 
                    certain cookies may affect the functionality of our platform.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">8. Third-Party Links</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  Our platform may contain links to third-party websites. We are not responsible 
                  for the privacy practices or content of these external sites. We encourage you 
                  to review their privacy policies before providing any information.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">9. Children's Privacy</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  Our platform is strictly for users 18 years and older. We do not knowingly 
                  collect or maintain information from anyone under 18. If we discover that we 
                  have collected information from a minor, we will delete it immediately.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">10. International Users</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  Our servers are located in India. If you access our platform from outside India, 
                  your information may be transferred to and processed in India, where data 
                  protection laws may differ from your country.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">11. Changes to This Policy</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300">
                  We may update this Privacy Policy from time to time. We will notify you of any 
                  material changes by posting the new policy on this page and updating the 
                  "Last Updated" date. Your continued use of the platform after changes 
                  constitutes acceptance of the updated policy.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">12. Contact Us</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">
                  If you have any questions or concerns about this Privacy Policy, please contact us:
                </p>
                <ul className="text-gray-400 space-y-1">
                  <li><span className="text-white">Privacy Officer:</span> privacy@uplino.in</li>
                  <li><span className="text-white">General Support:</span> support@uplino.in</li>
                  <li><span className="text-white">Data Requests:</span> data-requests@uplino.in</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">13. Grievance Officer</h2>
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">
                  In accordance with Information Technology Act 2000 and rules thereunder, 
                  the name and contact details of the Grievance Officer are provided below:
                </p>
                <ul className="text-gray-400 space-y-1">
                  <li><span className="text-white">Name:</span> Grievance Officer, uplino.in</li>
                  <li><span className="text-white">Email:</span> grievance@uplino.in</li>
                  <li><span className="text-white">Response Time:</span> Within 24 hours of receiving complaint</li>
                </ul>
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Privacy;
